/**
 * Quantities a surveyor types: feet of cable, inches of trunk.
 *
 * The pattern is shared with the input schema rather than written twice,
 * because the two disagreeing is not a cosmetic problem here. A tree write is
 * validated as ONE batch: a single operation the server rejects fails every
 * other queued edit for that site with it, and a 400 is not retriable, so a
 * value the client was willing to commit but the server will not accept wedges
 * the whole outbox until somebody notices.
 */

/**
 * Up to four digits and two decimal places.
 *
 * Four digits because no tree has a five-digit measurement in either unit, and
 * a typed one is a slipped finger rather than a very large tree. Two places
 * because that is what the `Decimal(_, 2)` columns hold — a third would be
 * silently rounded away, and a quantity that does not round-trip is a quantity
 * somebody will later find changed.
 */
export const QUANTITY_PATTERN = /^\d{1,4}(\.\d{1,2})?$/

/**
 * Parse a typed quantity, or null if it is not one.
 *
 * Strict for the same reason `parseMinutes` is: `Number.parseFloat` accepts
 * "12.345ft" and quietly returns 12.345, and a value that does not round-trip
 * is a mistake being carried forward into a price.
 *
 * Returns the STRING, not a number — it is bound for a `Decimal` column and
 * must not pass through a float on the way, the same rule money follows here.
 */
export function parseQuantity(value: string | null): string | null {
  if (value === null) return null
  const trimmed = value.trim()
  if (!QUANTITY_PATTERN.test(trimmed)) return null
  return trimmed
}

/**
 * Whether a half-typed value could still become a valid one.
 *
 * "14." is what every decimal is on its way through, and a field that rejected
 * it would be a field nobody could type a decimal into. This lets a form hold
 * an incomplete value without committing it — which is the distinction that
 * keeps the unfinished string out of the sync queue.
 */
export function isPartialQuantity(value: string): boolean {
  return value.trim() === '' || /^\d{0,4}(\.\d{0,2})?$/.test(value.trim())
}
